/** @format */

import React from "react";
import { Box, Button, Chip, Paper, Typography } from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import { Stack } from "rsuite";

const ACTION_COLOR = {
  CREATED: "info",
  APPROVED: "success",
  SENT_BACK: "error",
  REASSIGNED: "warning",
  DROPPED: "error",
  DOCUMENT_UPDATED: "info",
  INPUTS_UPDATED: "info",
  FILE_REPLACED: "info",
  MOVED: "warning",
  SKIPPED: "warning",
  STORE_CREATED: "success",
  REMINDER_SENT: "secondary",
  AUTO_APPROVED: "warning",
};

const ACTION_VERB = {
  CREATED: "Created",
  APPROVED: "Approved",
  SENT_BACK: "Sent back",
  REASSIGNED: "Reassigned",
  DROPPED: "Dropped",
  DOCUMENT_UPDATED: "Document updated",
  INPUTS_UPDATED: "Inputs updated",
  FILE_REPLACED: "File replaced",
  MOVED: "Moved",
  SKIPPED: "Skipped",
  STORE_CREATED: "Store created",
  REMINDER_SENT: "Reminder sent",
  AUTO_APPROVED: "Auto-approved (stale)",
};

// System-generated stepHistory entries (e.g. REMINDER_SENT) are recorded with
// performedBy = 0 (no real acting user) — see SYSTEM_USER_ID in the API's cf-v2.constants.js.
const SYSTEM_USER_ID = 0;

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      mb: 2,
      color: "#008938",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const CfV2StepHistory = ({ stepHistory = [], stepNumberNameMap = {}, userDetailsMap = {} }) => (
  <Paper variant="outlined" sx={{ p: 3, height: "100%", maxHeight: 900, overflow: "auto" }}>
    <SectionTitle>Step History</SectionTitle>

    {stepHistory.length > 0 ? (
      <Timeline sx={{ p: 0, m: 0 }}>
        {[...stepHistory].reverse().map((step, index, reversedHistory) => (
          <TimelineItem key={index} sx={{ "&::before": { display: "none" } }}>
            <TimelineSeparator>
              <TimelineDot color={ACTION_COLOR[step.action] || "grey"} />
              {index < reversedHistory.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: 2, pt: 0.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" >
                <Typography
                  variant="caption"
                  display="block"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    mb: 0.25,
                  }}
                >
                  Step {`${step.stepNumber} - ${stepNumberNameMap[step.stepNumber] || step.stepNumber}`}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.25 }}>
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: `${ACTION_COLOR[step.action] || "text.primary"}.main`,
                    }}
                  >
                    <Chip
                      label={ACTION_VERB[step.action] || step.action}
                      size="small"
                      color={ACTION_COLOR[step.action] || "default"}
                    />

                  </Box>
                  {" by "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {step.performedBy === SYSTEM_USER_ID
                      ? "System"
                      : userDetailsMap[step.performedBy] || step.performedBy}
                  </Box>
                  {" on "}
                  {moment(step?.timestamp).format("DD MMM YYYY, hh:mm A")}

                </Typography>
              </Stack>
              {step.comment && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    fontStyle: "italic",
                    color: "text.secondary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  "{step.comment}"
                </Typography>
              )}
              {step.sentToStep != null && (
                <Typography
                  variant="caption"
                  color={step.action === "MOVED" ? "warning.main" : "error.main"}
                  display="block"
                >
                  {step.action === "MOVED" ? "Moved to Step " : "Sent back to Step "}
                  {stepNumberNameMap[step.sentToStep] || step.sentToStep}
                </Typography>
              )}
              {step.skipedReasonDoc && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    href={step.skipedReasonDoc}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon />}
                  >
                    View reason file
                  </Button>
                </Box>
              )}
              {step.attachments?.unitEconomics && (
                <Box sx={{ mt: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    href={step.attachments.unitEconomics}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<OpenInNewIcon />}
                  >
                    View Unit Economics document
                  </Button>
                </Box>
              )}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No step history available.
      </Typography>
    )}
  </Paper>
);

export default CfV2StepHistory;
